import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadDashboardLayout, resetDashboardLayout, saveDashboardLayout } from './dashboardLayoutService.js';
import { defaultLayoutFor } from './dashboardDefaults.js';
import { resolveLoadedLayout } from './layoutStorage.js';

/**
 * Loads / saves one dashboard's layout for one user in one organization (Beta-1 plan P3-19).
 *
 *   const saved = useDashboardLayout({ dashboardKey: 'drew-oe', uid, orgId, role, registry });
 *   const editor = useDashboardEditor(saved.layout, { onDone: (l, { dirty }) => dirty && saved.save(l) });
 *
 * Options
 *   dashboardKey, uid, orgId, role     required to load; while any is missing the role default shows
 *   registry                           WidgetRegistry
 *   widgetContext                      extra availability context (feature flags…)
 *   readOnly                           View As (P3-11): load `uid`'s layout but never write
 *   onDropped(ids)                     called once when a saved layout had unknown widget ids
 *
 * Returns { layout, loading, source ('default' | 'saved'), save(layout), reset(), saving, error }.
 * Reads: one getDoc per (uid, orgId, dashboardKey). Writes: only from save()/reset().
 */
export function useDashboardLayout({ dashboardKey, uid, orgId, role, registry, widgetContext = null, readOnly = false, onDropped }) {
  const ctx = useMemo(() => ({ dashboardKey, role, registry, ...(widgetContext || {}) }), [dashboardKey, role, registry, widgetContext]);
  const [state, setState] = useState(() => ({
    layout: defaultLayoutFor(dashboardKey, role, registry, widgetContext || {}),
    source: 'default',
    loading: Boolean(uid && orgId),
    error: null,
  }));
  const [saving, setSaving] = useState(false);
  const onDroppedRef = useRef(onDropped);
  onDroppedRef.current = onDropped;

  useEffect(() => {
    let cancelled = false;
    if (!uid || !orgId || !dashboardKey) {
      setState({ layout: defaultLayoutFor(dashboardKey, role, registry, widgetContext || {}), source: 'default', loading: false, error: null });
      return undefined;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    loadDashboardLayout(uid, orgId, dashboardKey)
      .then((stored) => {
        if (cancelled) return;
        const resolved = resolveLoadedLayout(stored, ctx);
        setState({ layout: resolved.layout, source: resolved.source, loading: false, error: null });
        if (resolved.dropped.length && onDroppedRef.current) onDroppedRef.current(resolved.dropped);
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn('Could not load dashboard layout:', error?.message || error);
        setState({ layout: defaultLayoutFor(dashboardKey, role, registry, widgetContext || {}), source: 'default', loading: false, error });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, orgId, dashboardKey, role, registry]);

  const save = useCallback(
    async (layout) => {
      setState((s) => ({ ...s, layout, source: 'saved' }));
      if (readOnly || !uid || !orgId) return false;
      setSaving(true);
      try {
        await saveDashboardLayout(uid, orgId, dashboardKey, layout);
        return true;
      } catch (error) {
        console.error('Could not save dashboard layout:', error);
        setState((s) => ({ ...s, error }));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [readOnly, uid, orgId, dashboardKey]
  );

  const reset = useCallback(async () => {
    const layout = defaultLayoutFor(dashboardKey, role, registry, widgetContext || {});
    setState((s) => ({ ...s, layout, source: 'default' }));
    if (readOnly || !uid || !orgId) return layout;
    setSaving(true);
    try {
      await resetDashboardLayout(uid, orgId, dashboardKey);
    } catch (error) {
      console.error('Could not reset dashboard layout:', error);
      setState((s) => ({ ...s, error }));
    } finally {
      setSaving(false);
    }
    return layout;
  }, [readOnly, uid, orgId, dashboardKey, role, registry, widgetContext]);

  return { ...state, save, reset, saving, readOnly };
}

export default useDashboardLayout;
